/*
 *  This content is licensed according to the W3C Software License at
 *  https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 *  The original W3 component created the `option` elements in the JS - given that is
 *  less sustainable for us, this component has been adapted so that the `option`s can
 *  exist in the Liquid/HTML
 *
 *  if you want to dispatch an event when an option is selected, a good place would be
 *  within the `selectOption` function, something like
 *   const customEvent = new Event('countryChanged')
 *   document.dispatchEvent(customEvent)
 *  and then you could listen to that event in another component
 */

'use strict'

// Save a list of named combobox actions, for future readability
const SelectActions = {
  Close: 0,
  CloseSelect: 1,
  First: 2,
  Last: 3,
  Next: 4,
  Open: 5,
  PageDown: 6,
  PageUp: 7,
  Previous: 8,
  Select: 9,
  Type: 10,
}

/*
 * Helper functions
 */

// filter an array of options against an input string
// returns an array of options that begin with the filter string, case-independent
const filterOptions = (options = [], filter, exclude = []) => {
  return options.filter((option) => {
    const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0
    return matches && exclude.indexOf(option) < 0
  })
}

// map a key press to an action
const getActionFromKey = (event, menuOpen) => {
  const { key, altKey, ctrlKey, metaKey } = event
  const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' '] // all keys that will do the default open action
  // handle opening when closed
  if (!menuOpen && openKeys.includes(key)) {
    return SelectActions.Open
  }

  // home and end move the selected option when open or closed
  if (key === 'Home') {
    return SelectActions.First
  }
  if (key === 'End') {
    return SelectActions.Last
  }

  // handle typing characters when open or closed
  if (
    key === 'Backspace' ||
    key === 'Clear' ||
    (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
  ) {
    return SelectActions.Type
  }

  // handle keys when open
  if (menuOpen) {
    if (key === 'ArrowUp' && altKey) {
      return SelectActions.CloseSelect
    } else if (key === 'ArrowDown' && !altKey) {
      return SelectActions.Next
    } else if (key === 'ArrowUp') {
      return SelectActions.Previous
    } else if (key === 'PageUp') {
      return SelectActions.PageUp
    } else if (key === 'PageDown') {
      return SelectActions.PageDown
    } else if (key === 'Escape') {
      return SelectActions.Close
    } else if (key === 'Enter' || key === ' ') {
      return SelectActions.CloseSelect
    }
  }
}

// return the index of an option from an array of options, based on a search string
// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
const getIndexByLetter = (options, filter, startIndex = 0) => {
  const orderedOptions = [
    ...options.slice(startIndex),
    ...options.slice(0, startIndex),
  ]
  const firstMatch = filterOptions(orderedOptions, filter)[0]
  const allSameLetter = (array) => array.every((letter) => letter === array[0])

  // first check if there is an exact match for the typed string
  if (firstMatch) {
    return options.indexOf(firstMatch)
  }

  // if the same letter is being repeated, cycle through first-letter matches
  else if (allSameLetter(filter.split(''))) {
    const matches = filterOptions(orderedOptions, filter[0])
    return options.indexOf(matches[0])
  }

  // if no matches, return -1
  else {
    return -1
  }
}

// get an updated option index after performing an action
const getUpdatedIndex = (currentIndex, maxIndex, action) => {
  const pageSize = 10 // used for pageup/pagedown

  switch (action) {
    case SelectActions.First:
      return 0
    case SelectActions.Last:
      return maxIndex
    case SelectActions.Previous:
      return Math.max(0, currentIndex - 1)
    case SelectActions.Next:
      return Math.min(maxIndex, currentIndex + 1)
    case SelectActions.PageUp:
      return Math.max(0, currentIndex - pageSize)
    case SelectActions.PageDown:
      return Math.min(maxIndex, currentIndex + pageSize)
    default:
      return currentIndex
  }
}

// check if element is visible in browser view port
const isElementInView = (element) => {
  const bounding = element.getBoundingClientRect()

  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <=
      (window.innerWidth || document.documentElement.clientWidth)
  )
}

// check if an element is currently scrollable
const isScrollable = (element) => {
  return element && element.clientHeight < element.scrollHeight
}

// ensure a given child element is within the parent's visible scroll area
// if the child is not visible, scroll the parent
const maintainScrollVisibility = (activeElement, scrollParent) => {
  const { offsetHeight, offsetTop } = activeElement
  const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent

  const isAbove = offsetTop < scrollTop
  const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight

  if (isAbove) {
    scrollParent.scrollTo(0, offsetTop)
  } else if (isBelow) {
    scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight)
  }
}

/*
 * Select Component
 * Accepts a combobox element and an array of string options
 */

class CustomDropdown extends HTMLElement {
  constructor() {
    super()
    // element refs
    this.el = this
    this.comboEl = this.el.querySelector('[role=combobox]')
    this.listboxEl = this.el.querySelector('[role=listbox]')

    // data
    this.idBase = this.comboEl.id || 'combo'
    this.options = [...this.el.querySelectorAll('.js-combo-option')]
    this.type = this.dataset.type

    this.multiselectable = this.listboxEl.hasAttribute('aria-multiselectable')

    // state
    this.activeIndex = 0
    this.open = false
    this.searchString = ''
    this.searchTimeout = null

    // init
    if (this.el && this.comboEl && this.listboxEl) {
      this.init()
    }

    if (this.multiselectable) {
      document.addEventListener('click', this.handleOutsideEvent.bind(this))
    }

    // mobile filters have a max height, so hide scroll bar if dropdown is not as tall as max height
    if (this.type === 'filter') {
      if (this.options.length * 60 < 200) {
        this.listboxEl.style.overflowY = 'auto'
      }
    }
  }

  init() {
    // select first option by default
    if (!this.multiselectable) {
      this.comboEl.innerHTML = this.options.find((option) =>
        option.classList.contains('combo-option--current')
      )?.innerText
    }

    // add event listeners
    this.comboEl.addEventListener('blur', this.onComboBlur.bind(this))
    this.comboEl.addEventListener('click', this.onComboClick.bind(this))
    this.comboEl.addEventListener('keydown', this.onComboKeyDown.bind(this))

    // create options
    this.options.forEach((option, index) => {
      option.addEventListener('click', (event) => {
        event.stopPropagation()

        // for the PLP filters, the stopPropagation isn't stopping the event from firing twice,
        // on both the label and the input. this ensures it only fires once.
        if (!event.target.classList.contains('filter__checkbox')) {
          this.onOptionClick(index)
        }
      })
      option.addEventListener('mousedown', this.onOptionMouseDown.bind(this))
    })
  }

  handleOutsideEvent(e) {
    if (!this.contains(e.target)) {
      this.updateMenuState(false)
    }
  }

  getSearchString(char) {
    // reset typing timeout and start new timeout
    // this allows us to make multiple-letter matches, like a native select
    if (typeof this.searchTimeout === 'number') {
      window.clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = window.setTimeout(() => {
      this.searchString = ''
    }, 500)

    // add most recent letter to saved search string
    this.searchString += char
    return this.searchString
  }

  onComboBlur() {
    // do not do blur action if ignoreBlur flag has been set
    if (this.ignoreBlur) {
      this.ignoreBlur = false
      return
    }

    // select current option and close
    if (this.open) {
      this.updateMenuState(false, false)

      if (!this.multiselectable) {
        this.selectOption(this.activeIndex)
      }
    }
  }

  onComboClick() {
    this.updateMenuState(!this.open, false)
  }

  onComboKeyDown(event) {
    const { key } = event
    const max = this.options.length - 1

    const action = getActionFromKey(event, this.open)

    switch (action) {
      case SelectActions.Last:
      case SelectActions.First:
        this.updateMenuState(true)
      // intentional fallthrough
      case SelectActions.Next:
      case SelectActions.Previous:
      case SelectActions.PageUp:
      case SelectActions.PageDown:
        event.preventDefault()
        return this.onOptionChange(
          getUpdatedIndex(this.activeIndex, max, action)
        )
      case SelectActions.CloseSelect:
        event.preventDefault()
        this.selectOption(this.activeIndex)
      // intentional fallthrough
      case SelectActions.Close:
        event.preventDefault()
        return this.updateMenuState(false)
      case SelectActions.Type:
        return this.onComboType(key)
      case SelectActions.Open:
        event.preventDefault()
        return this.updateMenuState(true)
    }
  }

  onComboType(letter) {
    // open the listbox if it is closed
    this.updateMenuState(true)

    // find the index of the first matching option
    const searchString = this.getSearchString(letter)
    const searchIndex = getIndexByLetter(
      this.options,
      searchString,
      this.activeIndex + 1
    )

    // if a match was found, go to it
    if (searchIndex >= 0) {
      this.onOptionChange(searchIndex)
    }
    // if no matches, clear the timeout and search string
    else {
      window.clearTimeout(this.searchTimeout)
      this.searchString = ''
    }
  }

  onOptionChange(index) {
    // update state
    this.activeIndex = index

    // update aria-activedescendant
    this.comboEl.setAttribute(
      'aria-activedescendant',
      `${this.idBase}-option-${index}`
    )

    // update active option styles
    const options = this.el.querySelectorAll('.js-combo-option')
    ;[...options].forEach((optionEl) => {
      optionEl.classList.remove('combo-option--current')
    })
    options[index].classList.add('combo-option--current')

    // ensure the new option is in view
    if (isScrollable(this.listboxEl)) {
      maintainScrollVisibility(options[index], this.listboxEl)
    }

    // ensure the new option is visible on screen
    // ensure the new option is in view
    if (!isElementInView(options[index])) {
      options[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  onOptionClick(index) {
    this.onOptionChange(index)
    this.selectOption(index)
    if (!this.multiselectable) {
      this.updateMenuState(false)
    }
  }

  onOptionMouseDown() {
    // Clicking an option will cause a blur event,
    // but we don't want to perform the default keyboard blur action
    this.ignoreBlur = true
  }

  selectOption(index) {
    // update state
    this.activeIndex = index

    // update displayed value
    const selected = this.options[index]
    if (!this.multiselectable) {
      this.comboEl.innerHTML = selected.innerText
    }

    const wasSelected = this.el.querySelector('[aria-selected="true"]')
    const options = this.el.querySelectorAll('.js-combo-option')

    // update aria-selected
    if (!this.multiselectable) {
      wasSelected.setAttribute('aria-selected', 'false')
      options[index].setAttribute('aria-selected', 'true')
    } else {
      const currentStatus = options[index].getAttribute('aria-selected')
      const newStatus = currentStatus === 'false'
      options[index].setAttribute('aria-selected', newStatus)
      options[index].classList[newStatus ? 'add' : 'remove'](
        'combo-option--current'
      )
      this.ignoreBlur = false
    }

    if (this.type === 'product-bundle') {
      document.dispatchEvent(
        new CustomEvent('bundleProductChanged', {
          bubbles: true,
          detail: {
            value: selected.getAttribute('aria-controls'),
          },
        })
      )
    }

    if (this.type === 'filter') {
      document.dispatchEvent(
        new CustomEvent('filterChosen', {
          bubbles: true,
          detail: {
            value: this.el,
          },
        })
      )
    }
  }

  updateMenuState(open, callFocus = true) {
    if (this.open === open) {
      return
    }

    // update state
    this.open = open

    // update aria-expanded and styles
    this.comboEl.setAttribute('aria-expanded', `${open}`)
    open ? this.el.classList.add('open') : this.el.classList.remove('open')

    // update activedescendant
    const activeID = open ? `${this.idBase}-option-${this.activeIndex}` : ''
    this.comboEl.setAttribute('aria-activedescendant', activeID)

    if (activeID === '' && !isElementInView(this.comboEl)) {
      this.comboEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }

    // move focus back to the combobox, if needed
    callFocus && this.comboEl.focus()
  }
}

window.customElements.define('custom-dropdown', CustomDropdown)
